// **Feedback** — Fehlermeldungen und Wünsche aus allen Mandanten (docs/11 8f).
//
// Die Meldungen liegen in der Feedback-API; die Plattform liest und bearbeitet
// sie dort als Admin des jeweiligen Mandanten. Der Kunde sieht Kommentare und
// Statuswechsel im eigenen Dialog, gezeichnet „<Name> (Solutec)".
//
// Die Filter stehen in der Adresse (`?tenant=&type=&status=`), ersetzt statt
// angehängt — ein Filterwechsel ist kein Ort, zu dem „Zurück" führen soll.

import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { get, put } from "../../api/client";
import { keys } from "../../api/queries";
import type { FeedbackList, FeedbackOverview } from "../../api/types";
import { PageHead } from "../../app/page-head";
import { fmtDate } from "../../lib/format";
import { useNavigate, useSearchParams, useSetSearch } from "../../router/router";
import { ErrorNote, Note } from "../../ui/badge";
import { Card } from "../../ui/card";
import { SelectMenu } from "../../ui/select-menu";
import { toast } from "../../ui/toaster";
import { StatusBadge, TypeBadge } from "./badges";
import { STATUS_OPTIONS, TYPE_OPTIONS, feedbackFail, filterFromSearch, listQuery, type FeedbackFilter } from "./feedback-model";

export function FeedbackListView() {
  const overview = useQuery({ queryKey: keys.feedbackOverview, queryFn: () => get<FeedbackOverview>("/feedback/overview") });
  const ov = overview.data;
  return (
    <>
      <PageHead title="Feedback" hint="Fehlermeldungen und Wünsche aus allen Mandanten." />
      {overview.isError ? <ErrorNote error={overview.error} /> : null}
      {ov && !ov.enabled ? (
        <Card title="Feedback"><Note tone="warn">Die Feedback-API ist nicht eingerichtet (FEEDBACK_API_URL, FEEDBACK_API_PUBLIC_KEY).</Note></Card>
      ) : null}
      {ov?.enabled ? <><Reports overview={ov} /><Notify overview={ov} /></> : null}
    </>
  );
}

function Reports({ overview }: { overview: FeedbackOverview }) {
  const navigate = useNavigate();
  const setSearch = useSetSearch();
  const filter = filterFromSearch(useSearchParams());
  const list = useQuery({ queryKey: keys.feedbackList({ ...filter }), queryFn: () => get<FeedbackList>(`/feedback?${listQuery(filter)}`) });
  // Den Status auch leer schreiben: „alle" ist `status=`, ein fehlender Status „offen".
  const change = (k: keyof FeedbackFilter, v: string) => setSearch({ [k]: v }, { keepEmpty: k === "status" ? ["status"] : [] });
  const tenantOptions: ReadonlyArray<readonly [string, string]> = [
    ["", "alle Mandanten"],
    ...(overview.tenants ?? []).map((t) => [t.slug, t.name + (t.disabled ? " (abgeschaltet)" : "")] as const),
  ];
  const data = list.data;
  return (
    <Card
      title="Meldungen"
      flush
      actions={(
        <div className="pf-filters">
          <SelectMenu name="tenant" ariaLabel="Mandant" value={filter.tenant} options={tenantOptions} onChange={(v) => change("tenant", v)} />
          <SelectMenu name="type" ariaLabel="Art" value={filter.type} options={TYPE_OPTIONS} onChange={(v) => change("type", v)} />
          <SelectMenu name="status" ariaLabel="Status" value={filter.status} options={STATUS_OPTIONS} onChange={(v) => change("status", v)} />
        </div>
      )}
    >
      <div>
        {list.isError ? <Note tone="error">{feedbackFail(list.error)}</Note> : null}
        {!data && !list.isError ? <div className="pf-empty">Wird geladen …</div> : null}
        {data ? (data.reports.length ? (
          <>
            <div className="pf-table-wrap">
              <table className="pf-table">
                <thead><tr><th>Meldung</th><th className="pf-hide-phone">Mandant</th><th>Status</th><th className="pf-num pf-hide-phone">Kommentare</th><th className="pf-hide-phone">Gemeldet</th></tr></thead>
                <tbody>
                  {data.reports.map((r) => {
                    const href = `/feedback/${r.id}`;
                    return (
                      <tr key={r.id} data-href={href} onClick={() => navigate(href)}>
                        <td><TypeBadge type={r.type} /> <strong>{r.title}</strong><div className="pf-sub">{r.author?.name || r.author?.email || ""}</div></td>
                        <td className="pf-hide-phone">{r.tenant?.name ?? r.tenant?.slug ?? ""}</td>
                        <td><StatusBadge status={r.status} /></td>
                        <td className="pf-num pf-hide-phone">{r.comment_count}</td>
                        <td className="pf-hide-phone pf-sub">{fmtDate(r.created_at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {data.total > data.reports.length ? <div className="pf-sub pf-mp__more">{data.total - data.reports.length} weitere — Filter eingrenzen</div> : null}
          </>
        ) : <div className="pf-empty">Keine Meldung passt zu diesem Filter.</div>) : null}
      </div>
    </Card>
  );
}

function Notify({ overview }: { overview: FeedbackOverview }) {
  const [on, setOn] = useState(Boolean(overview.notify));
  const toggle = async (next: boolean) => {
    setOn(next);
    try {
      await put("/me/notify", { notify: next });
      toast(next ? "Benachrichtigungen an" : "Benachrichtigungen aus");
    } catch (err) {
      // Der Haken steht wieder, wo der Server ihn hat.
      setOn(!next);
      toast(feedbackFail(err), "error");
    }
  };
  const n = overview.recipients ?? 0;
  return (
    <Card
      title="Benachrichtigungen"
      hint={overview.mail
        ? `Neue Meldungen und Kommentare gehen per Mail an ${n} ${n === 1 ? "Plattform-Admin" : "Plattform-Admins"} — an jeden, der sich hier angemeldet hat und sie nicht abgeschaltet hat.`
        : "Kein Mailversand eingerichtet (SMTP_HOST) — Benachrichtigungen gehen nicht hinaus."}
    >
      <label className="pf-check">
        <input type="checkbox" checked={on} disabled={!overview.mail} onChange={(e) => void toggle(e.target.checked)} />
        <span className="pf-check__text"><span>Mails an mich</span><span className="pf-check__hint">bei neuem Feedback und neuen Kommentaren aus den Mandanten</span></span>
      </label>
    </Card>
  );
}
